import React, {useMemo, useRef, ReactElement, useContext} from 'react';
import {useIntl} from 'react-intl';
import classname from 'classnames';

import {ScrollingBodyModal} from '@homeaway/react-modal';
import {useModalRoute} from '@homeaway/react-router-modal';
import {FILTER__16} from '@homeaway/svg-defs';
import {
    TrackingContextValue,
    TrackingContext,
} from '@homeaway/react-event-tracking';
import FilterPill from '../FilterPill/FilterPill';
import FilterGroupContent from '../FilterGroupContent/FilterGroupContent';
import FilterGroupFooter from '../FilterGroupFooter/FilterGroupFooter';

import {MIN_RESULTS_TO_SEARCH} from '../../utils/constants';
import {getAppliedCount} from '../../utils/filterPillUtils';
import {getEventParams} from '../../utils/filters';

import './MoreFiltersModal.less';
import {
    FILTER_GROUP_MORE_FILTERS_PRESENTED,
    // FILTER_GROUP_MORE_FILTERS_HIDDEN,
    PRICE_FILTER_SUBMITTED,
} from '../../utils/edap';

// The types in this file are listed here rather than being imported from other files
// This is intentional and necessary in order to generate documentation using `npm run start:docs`
// Per react-docgen (https://github.com/reactjs/react-docgen):
// "react-docgen will not be able to grab the type definition if the type is imported or declared in a different file."

type Filter = {
    description?: string;
    refineByQueryArgument: string;
    id: string;
    groupId?: string;
    name: string;
};

type Refinement = {
    count?: number;
    checked: boolean;
    filter: Filter;
};

type FilterGroupInfo = {
    id: string;
    name: string;
};

type FilterGroupType = {
    groupInfo: FilterGroupInfo;
    filters: Refinement[];
};

type CustomFilterParams = {
    selectedFilterByTotalPrice: boolean;
    appliedFilterByTotalPrice: boolean;
    appliedMaxNightlyPrice: number | null;
    appliedMinNightlyPrice: number | null;
    selectedMaxNightlyPrice: number | null;
    selectedMinNightlyPrice: number | null;
    selectedMaxTotalPrice: number | null;
    selectedMinTotalPrice: number | null;
    appliedMaxTotalPrice: number | null;
    appliedMinTotalPrice: number | null;
    selectedMinBedrooms: number;
    selectedMinBathrooms: number;
    appliedMinBedrooms: number;
    appliedMinBathrooms: number;
};

type ExtendedProps = {
    appliedFilters: string[];
    currency: string;
    customFiltersParams: CustomFilterParams;
    filterGroups: FilterGroupType[];
    isMobile: boolean;
    onClear: Function;
    onCustomFilterUpdate: Function;
    onDismiss: Function;
    onDone: Function;
    onFilterToggle: Function;
    onMultipleFilterToggle: Function;
    priceFilterOptions?: {
        isFilterByTotalPriceEnabled?: boolean;
        lengthOfStay?: number;
        showFilterByPriceType?: boolean;
    };
    priceInputValuesAreValid: boolean;
    results: number;
    selectedFilters: string[];
    updatePriceInputValuesValidity: Function;
};

interface MoreFiltersModalProps extends ExtendedProps {
    id?: string;
    sliceAt: number;
    allFiltersButtonCustomTrigger?: ({
        appliedCount: number,
        onClick: Function,
    }) => ReactElement;
}

/* eslint-disable complexity */
export default function MoreFiltersModal({
    allFiltersButtonCustomTrigger,
    appliedFilters,
    currency,
    customFiltersParams,
    filterGroups,
    isMobile,
    id = 'more-filters',
    onClear,
    onCustomFilterUpdate,
    onDone,
    onDismiss,
    onFilterToggle,
    onMultipleFilterToggle,
    priceFilterOptions,
    priceInputValuesAreValid,
    results,
    selectedFilters,
    sliceAt,
    updatePriceInputValuesValidity,
}: MoreFiltersModalProps) {
    const [isModalOpen, setModalOpen] = useModalRoute(id);
    const isModalDismissed = useRef(false);
    const edapContext: TrackingContextValue = useContext(TrackingContext);
    const {formatMessage} = useIntl();
    // Get filterGroups that should be rendered in the More filters modal
    const filterGroupsModal = useMemo<FilterGroupType[]>(
        () => filterGroups.slice(sliceAt, filterGroups.length),
        [filterGroups, sliceAt]
    );

    const filterGroupsRanked = filterGroupsModal
        .map((filterGroup) => filterGroup.groupInfo.id)
        .join(',');

    // Get all filters (refinements) part of the filterGroups
    const modalFilters = useMemo<Refinement[]>(
        () =>
            filterGroupsModal.reduce((curr, filterGroup) => {
                return curr.concat(filterGroup.filters);
            }, [] as Refinement[]),
        [filterGroupsModal]
    );

    const appliedCount = useMemo<number>(
        () =>
            getAppliedCount({
                appliedFilters,
                filterGroups: filterGroupsModal,
                filters: modalFilters,
                customFiltersParams,
            }),
        [modalFilters, appliedFilters, filterGroupsModal, customFiltersParams]
    );
    function triggerTotalPriceSubmitted() {
        const {pricesAreNotDefault, eventParams} = getEventParams(
            customFiltersParams
        );
        if (pricesAreNotDefault) {
            edapContext.trigger(PRICE_FILTER_SUBMITTED, eventParams);
        }
    }

    function onOpenModal() {
        edapContext.trigger(FILTER_GROUP_MORE_FILTERS_PRESENTED, {
            filtergroupid: filterGroupsRanked,
        });

        isModalDismissed.current = false;
        setModalOpen(true);
    }

    // TODO: Enable this once the spec for this EDAP event is fixed
    // function triggerEdapMoreFiltersHidden(actiontype: 'closed' | 'applied') {
    //     edapContext.trigger(FILTER_GROUP_MORE_FILTERS_HIDDEN, {
    //         filtergroupid: filterGroupsRanked,
    //         actiontype,
    //     });
    // }

    function handleModalClose() {
        setModalOpen(false);

        /* istanbul ignore else */
        if (!isModalDismissed.current) {
            isModalDismissed.current = true;
            if (results > MIN_RESULTS_TO_SEARCH) {
                onDone();
            } else {
                onDismiss();
            }

            triggerTotalPriceSubmitted();
            // TODO: Enable this once the spec for this EDAP event is fixed
            // triggerEdapMoreFiltersHidden('closed');
        }
    }

    function handleOnDone() {
        isModalDismissed.current = true;
        setModalOpen(false);
        onDone();

        triggerTotalPriceSubmitted();
        // TODO: Enable this once the spec for this EDAP event is fixed
        // triggerEdapMoreFiltersHidden('applied');
    }

    function renderFooter() {
        return (
            <FilterGroupFooter
                customFiltersParams={customFiltersParams}
                filterGroups={filterGroupsModal}
                onDone={handleOnDone}
                onClear={onClear}
                results={results}
                filters={modalFilters}
                selectedFilters={selectedFilters}
            />
        );
    }

    const label =
        // sliceAt === 0 means we are showing all filter groups
        sliceAt === 0
            ? formatMessage({
                  id: 'filtersDialogLabel',
                  defaultMessage: 'Filters',
              })
            : formatMessage({
                  id: 'moreFiltersLabel',
                  defaultMessage: 'More filters',
              });

    if (!filterGroupsModal.length) {
        return null;
    }

    return (
        <div className="MoreFiltersModal">
            {allFiltersButtonCustomTrigger ? (
                allFiltersButtonCustomTrigger({
                    appliedCount,
                    onClick: onOpenModal,
                })
            ) : (
                <FilterPill
                    applied={appliedCount > 0}
                    appliedCount={appliedCount}
                    onClick={onOpenModal}
                    leftAlign
                    icon={FILTER__16}
                    label={label}
                />
            )}
            <ScrollingBodyModal
                id="more-filters"
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onModalClose={handleModalClose}
                title={label}
                footer={renderFooter()}
                isWorkflow={isMobile}
                parentNode={
                    /* istanbul ignore next */
                    typeof document !== 'undefined' ? document.body : null
                }
                portalClassName="ScrollingBodyModalPortal"
            >
                {filterGroupsModal.map(({groupInfo, filters}, index) => {
                    const className = classname(
                        'MoreFiltersModal__filterGroup',
                        {
                            'MoreFiltersModal__filterGroup--last':
                                index + 1 === filterGroupsModal.length,
                        }
                    );

                    return (
                        <div className={className} key={groupInfo.id}>
                            <span className="MoreFiltersModal__groupLabel">
                                {groupInfo.name}
                            </span>
                            <FilterGroupContent
                                currency={currency}
                                customFiltersParams={customFiltersParams}
                                filters={filters}
                                groupId={groupInfo.id}
                                isMobile={isMobile}
                                isGroupOpen
                                selectedFilters={selectedFilters}
                                onCustomFilterUpdate={onCustomFilterUpdate}
                                onFilterToggle={onFilterToggle}
                                onMultipleFilterToggle={onMultipleFilterToggle}
                                priceFilterOptions={priceFilterOptions}
                                priceInputValuesAreValid={
                                    priceInputValuesAreValid
                                }
                                results={results}
                                twoColumns
                                updatePriceInputValuesValidity={
                                    updatePriceInputValuesValidity
                                }
                                rank={index + sliceAt}
                            />
                        </div>
                    );
                })}
            </ScrollingBodyModal>
        </div>
    );
}
